import { ConsumableLine, ConsumablePricingContext, ConsumablePricingResult } from '../consumable-models';
import { BaseConsumableRule } from '../consumable-rule-base';

/**
 * Rule: Partial Pack Dispensing
 * 
 * Handles cases where a doctor dispenses a partial quantity from a standard pack.
 * For example: 30 capsules from a 100-capsule pack, or 1 vial from a 10-vial pack.
 * 
 * Calculation:
 * - Base price per unit = singleExitPrice / standardPacksize
 * - Apply doctor markup (if not network doctor)
 * - Total = (price per unit) * quantity dispensed
 * Note: VAT is applied separately after pricing calculations
 */
export class RulePartialPackDispensing extends BaseConsumableRule {
    name = 'Partial Pack Dispensing';
    priority = 1; // Run first - this is the base calculation
    
    applies(line: ConsumableLine, ctx: ConsumablePricingContext): boolean {
        // This rule always applies to consumables with pack size information
        return line.codeType === 'consumable' && 
               line.standardPacksize > 0 && 
               line.singleExitPrice > 0;
    }
    
    apply(line: ConsumableLine, ctx: ConsumablePricingContext): ConsumablePricingResult {
        const warnings: string[] = [];
        
        // Calculate base price per unit
        const basePricePerUnit = this.calculateBasePricePerUnit(line);
        
        // Apply doctor markup if not a network doctor (VAT is applied separately)
        let pricePerUnit = basePricePerUnit;
        if (!ctx.isNetworkDoctor && ctx.modifier) {
            pricePerUnit = this.applyMarkup(pricePerUnit, ctx.modifier);
        }
        
        // Calculate total for the dispensed quantity
        const quantity = line.quantity || 1;
        const calculatedTotalPrice = pricePerUnit * quantity;
        
        // Warn if dispensing more than a full pack
        if (quantity > line.standardPacksize) {
            warnings.push(
                `Dispensing ${quantity} units exceeds standard pack size of ${line.standardPacksize}. ` +
                `Consider using multiple line items for multiple packs.`
            );
        }
        
        // Warn if dispensing very small quantities (might be a data entry error)
        if (quantity < 0.1 && line.standardPacksize > 1) {
            warnings.push(
                `Dispensing quantity (${quantity}) is very small compared to pack size (${line.standardPacksize}). ` +
                `Please verify this is correct.`
            );
        }
        
        const trace = [
            `Base price per unit: R${basePricePerUnit.toFixed(4)} (pack: R${line.singleExitPrice} / ${line.standardPacksize})`,
            ...(ctx.modifier && !ctx.isNetworkDoctor 
                ? [`After markup (${ctx.modifier}%): R${pricePerUnit.toFixed(4)}`]
                : [`Price per unit: R${pricePerUnit.toFixed(4)}`]),
            `Total for ${quantity} units: R${calculatedTotalPrice.toFixed(2)} (VAT will be applied separately)`
        ];
        
        return {
            line: { ...line },
            calculatedUnitPrice: pricePerUnit,
            calculatedTotalPrice,
            trace,
            warnings
        };
    }
}

